import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Eye, EyeSlash, Sparkle, Ticket, ArrowClockwise, Check } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExternalApiPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  // Batch generator
  const [genPlan, setGenPlan] = useState('quarterly');
  const [genQty, setGenQty] = useState(50);
  const [genCampaign, setGenCampaign] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/external/admin/key`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore recupero chiave');
    } finally { setLoading(false); }
  };

  const copy = (v, msg = 'Copiato!') => {
    navigator.clipboard.writeText(v);
    toast.success(msg);
  };

  const generateBatch = async () => {
    if (!genCampaign.trim()) {
      toast.error('Dai un nome alla campagna (es. instagram-marzo-26)');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/external/promo-codes/batch`, {
        plan_type: genPlan,
        quantity: parseInt(genQty),
        campaign: genCampaign.trim(),
        label: `Batch ${genCampaign.trim()}`,
      }, {
        headers: { 'X-API-Key': data.api_key },
      });
      setLastBatch(res.data);
      toast.success(`${res.data.quantity} codici generati!`);
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore generazione batch');
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="p-8 text-center text-zinc-400">Caricamento…</div>;
  if (!data) return null;

  const maskedKey = data.api_key ? `${data.api_key.slice(0, 12)}${'•'.repeat(20)}${data.api_key.slice(-4)}` : 'Non configurata';

  return (
    <div className="space-y-6" data-testid="external-api-panel">
      {/* Intro */}
      <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center flex-shrink-0">
            <Sparkle size={20} weight="fill" />
          </div>
          <div>
            <h3 className="text-lg font-black text-zinc-900 mb-1">API per Vitadahost.it</h3>
            <p className="text-sm text-zinc-600 leading-relaxed">
              Endpoint server-to-server per generare codici sconto univoci dai concorsi Instagram del sito principale.
              Ogni codice è single-use, con durata 1 mese / 3 mesi / 1 anno / a vita.
              La <strong>API key</strong> qui sotto va copiata nelle env di vitadahost.it come <code className="text-xs bg-white px-1 rounded">EXTERNAL_API_KEY</code>.
            </p>
          </div>
        </div>
      </div>

      {/* Key + Base URL */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="api-key-card">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-3">Credenziali</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">Base URL</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-mono text-zinc-800">{data.base_url}</code>
              <button onClick={() => copy(data.base_url, 'URL copiato')} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="copy-base-url-btn"><Copy size={16} /></button>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">X-API-Key</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-mono text-zinc-800 truncate">
                {showKey ? data.api_key : maskedKey}
              </code>
              <button onClick={() => setShowKey(!showKey)} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="toggle-key-btn" aria-label="Mostra/nascondi">
                {showKey ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={() => copy(data.api_key, 'Chiave copiata')} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="copy-key-btn"><Copy size={16} /></button>
            </div>
            <p className="text-[11px] text-amber-700 mt-2">⚠️ Non condividerla lato client (browser). Usala solo dal backend di vitadahost.it.</p>
          </div>

          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">Esempio curl</label>
            <div className="relative">
              <pre className="bg-zinc-900 text-emerald-300 rounded-lg p-3 text-[11px] font-mono overflow-x-auto"><code>{`curl -X POST ${data.base_url}/promo-codes/batch \\
  -H "X-API-Key: <TUA_CHIAVE>" \\
  -H "Content-Type: application/json" \\
  -d '{"plan_type":"quarterly","quantity":50,"campaign":"ig-marzo-26"}'`}</code></pre>
              <button
                onClick={() => copy(`curl -X POST ${data.base_url}/promo-codes/batch -H "X-API-Key: ${data.api_key}" -H "Content-Type: application/json" -d '{"plan_type":"quarterly","quantity":50,"campaign":"ig-marzo-26"}'`, 'Comando copiato')}
                className="absolute top-2 right-2 bg-zinc-800 text-emerald-300 rounded-lg p-1.5 hover:bg-zinc-700"
                data-testid="copy-curl-btn"
              ><Copy size={12} /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="external-stats-grid">
        {[
          { label: 'Totale generati', value: data.total_codes, color: 'text-zinc-900' },
          { label: 'Disponibili', value: data.available_codes, color: 'text-emerald-600' },
          { label: 'Usati', value: data.used_codes, color: 'text-teal-700' },
          { label: 'Disattivati', value: data.inactive_codes, color: 'text-rose-600' },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-zinc-200 rounded-2xl p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{k.label}</p>
            <p className={`text-3xl font-black ${k.color} mt-1 tabular-nums`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Batch generator */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="external-batch-form">
        <h4 className="text-sm font-black uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
          <Ticket size={16} /> Genera batch di codici
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">Tipo piano</label>
            <select value={genPlan} onChange={(e) => setGenPlan(e.target.value)} className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" data-testid="ext-plan-select">
              <option value="monthly">Mensile (30gg)</option>
              <option value="quarterly">Trimestrale (3 mesi)</option>
              <option value="yearly">Annuale (1 anno)</option>
              <option value="lifetime">A vita</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">Quantità</label>
            <input type="number" min={1} max={500} value={genQty} onChange={(e) => setGenQty(e.target.value)} className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" data-testid="ext-qty-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-semibold block mb-1">Nome campagna</label>
            <input type="text" value={genCampaign} onChange={(e) => setGenCampaign(e.target.value)} placeholder="es. ig-marzo-26" className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" data-testid="ext-campaign-input" />
          </div>
          <button
            onClick={generateBatch}
            disabled={generating}
            className="bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="ext-generate-btn"
          >
            {generating ? <ArrowClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
            {generating ? 'Genero…' : 'Genera codici'}
          </button>
        </div>
        {lastBatch && (
          <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="ext-last-batch">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-black text-emerald-900 flex items-center gap-2">
                <Check size={16} weight="bold" /> Batch {lastBatch.batch_id} — {lastBatch.quantity} codici {lastBatch.plan_type}
              </p>
              <button
                onClick={() => copy(lastBatch.codes.join('\n'), `${lastBatch.codes.length} codici copiati`)}
                className="text-xs text-emerald-800 font-bold hover:underline flex items-center gap-1"
                data-testid="ext-copy-batch-btn"
              >
                <Copy size={12} /> Copia tutti
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto bg-white rounded-lg border border-emerald-200 p-2 font-mono text-xs">
              {lastBatch.codes.map((c) => (
                <div key={c} className="flex items-center justify-between px-2 py-0.5 hover:bg-zinc-50 rounded">
                  <span>{c}</span>
                  <button onClick={() => copy(c, `${c} copiato`)} className="text-zinc-400 hover:text-zinc-900"><Copy size={10} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Campaigns */}
      {data.campaigns.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-5">
          <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-3">Campagne attive</p>
          <div className="flex flex-wrap gap-2">
            {data.campaigns.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 px-2.5 py-1 rounded-full text-xs font-bold">{c}</span>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-zinc-400 text-center">
        Documentazione tecnica completa in <code>/app/EXTERNAL_API.md</code>
      </div>
    </div>
  );
}
